import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { Att, AttrPath, Table, UitstroomtypeVSO } from '@cumlaude/metadata';
import { generateCssClassForString } from '@cumlaude/shared-utils';
import { last, range } from 'lodash-es';
import { Observable } from 'rxjs';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { countRecords, SingleAggregator, sumOver0 } from '../../services/aggregation';
import { Level, Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	defaultDoorstroomAvailableGroups,
	ExportFilter,
	FilterExpression,
} from '../../services/data.service';
import { att, percOfRow } from '../../services/measures';
import { BarInfo } from '../../services/stacked-bars';
import { DashboardVariant, VerschilBeoogd } from '../../services/weergave-opties';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { Attributes, BaseDashboardConfig, getLevelFilters } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { Axis } from '../../services/axis';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { formatNumber } from '@angular/common';
import { UitstroomniveauWaarde } from '../../services/label-enums';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

export interface BeoogdIrtBehaaldI extends Attributes {
	ds_fun_nr_abs_verschil_einduitstroomniveau_vso: string;
	ds_fun_nr_verschil_einduitstroomniveau_vso: string;
	['ds_fk_lb_van.lb_nr_uitstroomniveau_vso']: string;
	['ds_fk_lb_van.lb_nr_beoogd_uitstroomniveau_vso']: string;
}

export interface BeoogdIrtBehaaldA extends Attributes {
	waarde: number;
	onbekend: number;
	bekend: number;
}

@Component({
	selector: 'app-beoogd-irt-behaald',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './beoogd-irt-behaald.component.html',
	styleUrls: [],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		FilterBarComponent,
		VbarchartTableComponent,
		WeergaveOptieComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class BeoogdIrtBehaaldComponent extends BarchartTableConfig<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA> {
	private readonly dataService = inject(DataService);

	override availableGroups: AttrPath[] = [
		...defaultDoorstroomAvailableGroups,
		['ds_fk_lb_van', 'lb_nm_uitstroomniveau_vso'],
		['ds_fk_lb_van', 'lb_nm_beoogd_uitstroomniveau_vso'],
		['ds_fk_ll', 'll_des_uitschrijving'],
	];

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));
	historieSubgroups = computed(() => {
		return [...this.groeperingService.huidigeGroeperingen().slice(-1), ['ds_nm_schooljaar_van'] as AttrPath, ...this.subgroups()];
	});

	verschilbeoogd = this.qp.signal('verschilbeoogd');
	toonVerschil: Signal<boolean> = computed(
		() => this.verschilbeoogd() === VerschilBeoogd.VERSCHIL || this.verschilbeoogd() === VerschilBeoogd.ABSOLUUT
	);

	waardePath: Signal<AttrPath> = computed(() => {
		switch (this.verschilbeoogd()) {
			case VerschilBeoogd.ABSOLUUT:
				return ['ds_fun_nr_abs_verschil_einduitstroomniveau_vso'];
			case VerschilBeoogd.VERSCHIL:
				return ['ds_fun_nr_verschil_einduitstroomniveau_vso'];
			case VerschilBeoogd.BEOOGD:
				return ['ds_fk_lb_van', 'lb_nr_beoogd_uitstroomniveau_vso'];
			case VerschilBeoogd.BEHAALD:
				return ['ds_fk_lb_van', 'lb_nr_uitstroomniveau_vso'];
		}
	});

	waardeAtt: Signal<Att & keyof BeoogdIrtBehaaldI> = computed(() => this.waardePath().join('.') as Att & keyof BeoogdIrtBehaaldI);

	subgroups: Signal<AttrPath[]> = computed(() => [this.waardePath()]);

	readonly actueelFilters: FilterName[] = [
		'x_prestatieanalyse_ds_schooljaar',
		'ds_fk_br_vest_van.br_co_brin',
		'ds_fk_vs_van.vs_nm_vestiging',
		'ds_fk_ilt_van.ilt_nm_niveau',
		'ds_nr_leerjaar_van',
		'ds_nm_prestatieanalyse_vso_uitzondering',
		'ds_fk_ll.ll_des_uitschrijving',
	];

	readonly historieFilters: FilterName[] = [
		'x_doorstroom_schooljaar_historie', //
		'x_doorstroom_multiselect_schooljaar',
		...this.actueelFilters.slice(1),
	];

	readonly permanentFilterExpressions = [
		// Bij VSO speelt 1 oktober geen rol, leerlingen stromen het hele jaar in en uit. We laten dus gegevens zien van de plaatsing waarin de leerling daadwerkelijk uitstroomt.
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nm_uitstroomtype_vso'], UitstroomtypeVSO.EINDUITSTROOM),
		new BasicFilterExpression(['ds_fk_vs_van', 'vs_is_vso'], 1),
	];

	readonly factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({
			...options,
			r: range(0, this.groeperingService.huidigeAantalGroeperingen() + 1),
		});
	}

	variant = this.qp.signal('variant');

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	protected override singleAggregators: Partial<{
		[ai in keyof BeoogdIrtBehaaldA]: SingleAggregator<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA[ai]>;
	}> = {
		waarde: {
			...sumOver0(this.waardeAtt()),
			init: (i) => Number(i[this.waardeAtt()]) * i.count_records,
		},
		// We tellen onbekend als de waarde null is of 0, tenzij we verschillen tonen, dan is 0 toegestaan.
		onbekend: countRecords(
			(attrs: BeoogdIrtBehaaldI) => attrs[this.waardeAtt()] === null || (!this.toonVerschil() && Number(attrs[this.waardeAtt()]) === 0)
		),
		bekend: countRecords(
			(attrs: BeoogdIrtBehaaldI) => attrs[this.waardeAtt()] !== null && (this.toonVerschil() || Number(attrs[this.waardeAtt()]) !== 0)
		),
	};

	getBekendFilter(): FilterExpression {
		const onbekend = this.toonVerschil() ? [null] : [null, 0];
		return new BasicFilterExpression(this.waardePath(), onbekend, 'not in');
	}

	override partitionBarData(
		rowRoot: Level<BeoogdIrtBehaaldA, number[]>,
		_context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BaseDashboardConfig<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA>>
	): Path<BeoogdIrtBehaaldA, number[]>[][] {
		return [rowRoot.r.filter((path) => att<'bekend', BeoogdIrtBehaaldA>('bekend')(path) > 0)];
	}

	override makeBar(
		attrs: BeoogdIrtBehaaldI,
		path: Path<BeoogdIrtBehaaldA, number[]>,
		context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BaseDashboardConfig<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA>>
	): BarInfo {
		const waarde = Number(attrs[this.waardeAtt()]);
		const cssString = this.waardeAtt().includes('verschil')
			? waarde.toString().replace('-', 'minus_').replace('.', '_')
			: 'un_' + waarde.toString().replace('.', '_');
		const formatted = formatNumber(waarde, 'nl_NL', '1.0-1');
		const tooltip = this.waardeAtt().includes('verschil')
			? `${formatted} verschilpunten`
			: UitstroomniveauWaarde[cssString.toUpperCase() as keyof typeof UitstroomniveauWaarde];

		return {
			...super.makeBar(attrs, path, context),
			description: this.variant() === DashboardVariant.ACTUEEL ? `${tooltip}` : undefined,
			tooltip: `${tooltip}: ${attrs.count_records} leerlingen`,
			className: generateCssClassForString(cssString),
		};
	}

	override createMeasureColumns(
		context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BeoogdIrtBehaaldComponent>
	): ColumnDef<DataRow<BeoogdIrtBehaaldA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		return [
			createMeasureColumn('Gemiddelde', percOfRow('waarde', 'bekend'), { context, format: '1.1-1' }),
			this.createLeerlingColumn('Leerlingen', att('bekend'), { context }, undefined, [this.getBekendFilter()]),
			createMeasureColumn('Onbekend', att('onbekend'), { context, clickHandler: (path) => this.handleUitzonderingRedirect(path, context) }),
		];
	}

	protected handleUitzonderingRedirect(
		path: Path<BeoogdIrtBehaaldA, number[]>,
		context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BeoogdIrtBehaaldComponent>
	) {
		const params: { [key: string]: any } = {
			from: this.urlService.getFrom(),
		};

		for (const levelFilter of getLevelFilters(context, path)) {
			Object.assign(params, this.filterService.encodeFilterValue(<FilterName>levelFilter.attr.join('.'), [levelFilter.val]));
		}

		// Het uitzondering-dashboard toont standaard leerlingen met onbekend verschil.
		// Leerlingen bij wie het beoogde en/of behaalde uitstroomniveau onbekend is, worden daar dus standaard getoond.
		// Wanneer de weergave Beoogd/Behaald niveau is, dan willen we alleen de leerlingen van wie dat specifieke niveau onbekend is.
		// 0 is hierbij ook onbekend, dus we filteren op 0 of null.
		if (!this.toonVerschil()) {
			params[this.waardePath().join('.')] = '[0, null]';
		}

		this.urlService.redirectDoorklik([this.getUitzonderingUrl()], params);
	}

	getUitzonderingUrl(): string {
		return '/details/uitzondering/uitstroomniveau';
	}

	partitionMeasure: PartitionMeasure<BeoogdIrtBehaaldA> = {
		type: 'number',
		getValue: percOfRow('waarde', 'bekend'),
	};

	override getBarchartQty(
		path: Path<BeoogdIrtBehaaldA, number[]>,
		_context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BeoogdIrtBehaaldComponent>
	): number | null {
		return percOfRow('waarde', 'bekend')(path);
	}

	/**
	 * Grenzen zijn min/max van de data, maar laat tick 1 (en -1 bij VERSCHIL) wel altijd zien.
	 */
	override createXAxis(context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BeoogdIrtBehaaldComponent>): Axis {
		const values = context.dataRoot!.r.map((path) => percOfRow('waarde', 'bekend')(path));
		const least = this.verschilbeoogd() == VerschilBeoogd.VERSCHIL ? -1 : 0;
		const min = Math.min(least, ...values);
		const max = Math.max(1, ...values);
		const tmin = Math.ceil(min);
		const tmax = Math.floor(max);
		const ticks = range(tmin, tmax + 1).map((v) => ({ label: `${v}`, qty: v }));
		// extra -0.1 is om de linker tick altijd te laten zien
		return { min: Math.min(min, tmin - 0.1), max, ticks };
	}

	/**
	 * Grenzen zijn de gehele ticks die de data insluiten. Laat tick 1 (en -1 bij VERSCHIL) altijd zien.
	 */
	override createYAxis(context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BeoogdIrtBehaaldComponent>): Axis {
		const values = context.dataRoot!.r.flatMap((row) => last(row)!.xr!.map((path) => percOfRow('waarde', 'bekend')(path.slice(0, -1))));
		const least = this.verschilbeoogd() == VerschilBeoogd.VERSCHIL ? -1 : 0;
		const min = Math.floor(Math.min(least, ...values));
		const max = Math.ceil(Math.max(1, ...values));
		const ticks = range(min, max + 1).map((v) => ({ label: `${v}`, qty: v }));
		return { min, max, ticks };
	}

	protected readonly DashboardVariant = DashboardVariant;

	override enrichTableModel(
		_context: DashboardContext<BeoogdIrtBehaaldI, BeoogdIrtBehaaldA, BeoogdIrtBehaaldComponent>,
		tableModel: TableModel<DataRow<BeoogdIrtBehaaldA>>
	) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	protected readonly VerschilBeoogd = VerschilBeoogd;

	override getDisplayOptions(): ExportFilter[] {
		return [{ label: 'Weergave', value: this.verschilbeoogd() }];
	}
}
